import React, { useState, useEffect } from 'react';
import { Check, Loader2, Home, Crown, Factory } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchUserProfile } from '@/services/paymentService';
import { useAuth } from '@/context/AuthContext';
import { STAGING_STYLES } from '@/lib/roomConfig';
import type { AspectRatio, StagingStyle } from '@/types';

const STYLE_ICONS = {
  Home,
  Crown,
  Factory,
} as const;

export interface PropertyDetails {
  propertyType: string;
  propertyAreaSqm: string;
  propertyRooms: string;
  propertyPrice: string;
  propertyStatus: string;
}

interface SetupViewProps {
  name: string;
  address: string;
  isEditing?: boolean;
  onNameChange: (name: string) => void;
  onAddressChange: (address: string) => void;
  onCancel: () => void;
  onCreate: (aspectRatio: AspectRatio, stagingStyle: StagingStyle, propertyDetails?: PropertyDetails) => void;
}

export const SetupView: React.FC<SetupViewProps> = ({
  name,
  isEditing = false,
  onNameChange,
  onCancel,
  onCreate,
}) => {
  const { user } = useAuth();

  // Aspect ratio state
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('4:3');
  const [showAspectRatioSelection, setShowAspectRatioSelection] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSavingDefault, setIsSavingDefault] = useState(false);

  // Staging style state
  const [stagingStyle, setStagingStyle] = useState<StagingStyle>('modern');

  // Load user's default aspect ratio preference
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) {
        setIsLoadingProfile(false);
        setShowAspectRatioSelection(true);
        return;
      }

      try {
        const profile = await fetchUserProfile(user.id);
        if (profile) {
          if (profile.defaultAspectRatio) {
            // User has a default set - use it automatically
            setAspectRatio(profile.defaultAspectRatio);
            setShowAspectRatioSelection(false);
          } else {
            // No default - show selection
            setShowAspectRatioSelection(true);
          }
        } else {
          setShowAspectRatioSelection(true);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setShowAspectRatioSelection(true);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleCreate = async () => {
    // Save as default if checkbox is checked
    if (saveAsDefault && user) {
      setIsSavingDefault(true);
      try {
        const { updateDefaultAspectRatio } = await import('@/services/paymentService');
        await updateDefaultAspectRatio(user.id, aspectRatio);
      } catch (error) {
        console.error('Error saving default aspect ratio:', error);
      } finally {
        setIsSavingDefault(false);
      }
    }

    onCreate(aspectRatio, stagingStyle);
  };

  if (isLoadingProfile) {
    return (
      <div className="max-w-md mx-auto mt-8 flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-8 animate-slide-up">
      <Card>
        <CardHeader>
          <CardTitle>
            {isEditing ? 'Projekt bearbeiten' : 'Neues Projekt'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="projectName">Projektname (z.B. Eigentümername)</Label>
            <Input
              id="projectName"
              type="text"
              placeholder="z.B. Müller Wohnung"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              autoFocus
            />
          </div>

          {/* Aspect Ratio Selection - only shown if user has no default */}
          {showAspectRatioSelection && (
            <div className="space-y-3 pt-2">
              <Label>Bildformat für Generierungen</Label>

              <div className="space-y-2">
                {/* 4:3 Option */}
                <label
                  className={`relative flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    aspectRatio === '4:3'
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="aspectRatio"
                    checked={aspectRatio === '4:3'}
                    onChange={() => setAspectRatio('4:3')}
                    className="sr-only"
                  />
                  <div className="w-10 h-[30px] bg-muted rounded border border-muted-foreground/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-mono text-muted-foreground">4:3</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground">4:3 Querformat</p>
                    <p className="text-xs text-muted-foreground">Immobilienstandard</p>
                  </div>
                  {aspectRatio === '4:3' && (
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  )}
                </label>

                {/* 1:1 Option */}
                <label
                  className={`relative flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    aspectRatio === '1:1'
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="aspectRatio"
                    checked={aspectRatio === '1:1'}
                    onChange={() => setAspectRatio('1:1')}
                    className="sr-only"
                  />
                  <div className="w-8 h-8 bg-muted rounded border border-muted-foreground/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-mono text-muted-foreground">1:1</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground">1:1 Quadratisch</p>
                    <p className="text-xs text-muted-foreground">Für Social Media</p>
                  </div>
                  {aspectRatio === '1:1' && (
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  )}
                </label>
              </div>

              {/* Save as default checkbox */}
              <label className="flex items-center gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  className="rounded border-muted-foreground/30 text-primary focus:ring-primary"
                />
                <span className="text-sm text-muted-foreground">
                  Diese Einstellung als Standard merken
                </span>
              </label>
            </div>
          )}

          {/* Info when default is applied */}
          {!showAspectRatioSelection && (
            <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Bildformat: </span>
              {aspectRatio === '4:3' ? '4:3 Querformat' : '1:1 Quadratisch'}
              <span className="text-xs block mt-1">
                (Dein Standard-Format. Änderbar in den Einstellungen.)
              </span>
            </div>
          )}

          {/* Staging Style Selection */}
          <div className="space-y-3 pt-2">
            <Label>Einrichtungsstil für Staging</Label>

            <div className="space-y-2">
              {STAGING_STYLES.map((style) => {
                const IconComponent = STYLE_ICONS[style.icon as keyof typeof STYLE_ICONS];
                return (
                  <label
                    key={style.id}
                    className={`relative flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      stagingStyle === style.id
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    }`}
                  >
                    <input
                      type="radio"
                      name="stagingStyle"
                      checked={stagingStyle === style.id}
                      onChange={() => setStagingStyle(style.id)}
                      className="sr-only"
                    />
                    <div className="w-10 h-10 bg-muted rounded border border-muted-foreground/20 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground">{style.label}</p>
                      <p className="text-xs text-muted-foreground">{style.description}</p>
                    </div>
                    {stagingStyle === style.id && (
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex gap-3">
          <Button variant="ghost" onClick={onCancel} className="flex-1">
            Abbrechen
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name || isSavingDefault}
            className="flex-1"
          >
            {isSavingDefault && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? 'Speichern' : 'Projekt erstellen'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
